import { Accordion, AccordionItem, DataTableSkeleton } from '@carbon/react';
import { AllocationManager } from 'components/allocation-manager/allocation-tracts-table';
import { atom, useAtom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  AllocationManagerResponse,
  AllocationManagerVersionsResponse,
} from 'types/api-responses';
import styles from './allocation-manager-accordion.module.scss';

interface AllocationManagerAccordionProps {
  /** Allocation manager ID from the selected slot's NRI data. null when no AM exists. */
  allocationManagerId: number | null;
}

/**
 * Simplified Allocation Manager for the Current Development map panel.
 * Fetches AM versions, auto-selects the latest, renders the tracts table.
 * No map, no version selector, no header actions, no participation dropdown.
 * @param {AllocationManagerAccordionProps} props - The component props.
 * @return {JSX.Element} The allocation manager accordion.
 */
export const AllocationManagerAccordion: FC<
  AllocationManagerAccordionProps
> = ({ allocationManagerId }) => {
  const [selectedTractId, setSelectedTractId] = useState<number | null>(null);
  const [userOpened, setUserOpened] = useState(false);

  // Reset when AM changes
  useEffect(() => {
    setSelectedTractId(null);
  }, [allocationManagerId]);

  // Fetch versions
  const versionsAtom = useMemo(() => {
    if (!allocationManagerId) return atom(null);
    return createResourceAtom<AllocationManagerVersionsResponse>(
      `/allocation_managers/${allocationManagerId}/versions`
    );
  }, [allocationManagerId]);
  const versionsResult = useAtomValue(versionsAtom);

  const versionsLoading =
    versionsResult && 'loading' in versionsResult && versionsResult.loading;
  const versions = useMemo(() => {
    if (
      !versionsResult ||
      !('data' in versionsResult) ||
      !versionsResult.data ||
      'error' in versionsResult.data
    )
      return [];
    return versionsResult.data.versions ?? [];
  }, [versionsResult]);

  const latestVersion = versions.length ? versions[versions.length - 1] : null;

  // Fetch AM data for latest version
  const amAtom = useMemo(() => {
    if (!allocationManagerId || !latestVersion) return atom(null);
    return createResourceAtom<AllocationManagerResponse>(
      `/allocation_managers/${allocationManagerId}?version=${latestVersion}`
    );
  }, [allocationManagerId, latestVersion]);
  const [amResult] = useAtom(amAtom);

  const amLoading = amResult && 'loading' in amResult && amResult.loading;
  const amData = useMemo(() => {
    if (
      !amResult ||
      !('data' in amResult) ||
      !amResult.data ||
      'error' in amResult.data
    )
      return null;
    return amResult.data;
  }, [amResult]);

  if (!allocationManagerId) {
    return (
      <div className={styles.container}>
        <Accordion>
          <AccordionItem title="Allocation Manager" open={false}>
            <p className={styles.emptyMessage}>
              No Allocation Manager available
            </p>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Accordion>
        <AccordionItem
          title="Allocation Manager"
          open={userOpened}
          onHeadingClick={() => setUserOpened((prev) => !prev)}
        >
          {versionsLoading || amLoading ? (
            <DataTableSkeleton
              rowCount={5}
              columnCount={4}
              headers={[
                { header: 'Tract' },
                { header: 'Acreage' },
                { header: 'Factor' },
                { header: 'GIS Acreage' },
              ]}
            />
          ) : amData && !('error' in amData) ? (
            <AllocationManager
              allocationManagerData={amData}
              onLineItemClick={setSelectedTractId}
              selectedTractId={selectedTractId}
              selectedVersion={latestVersion ?? undefined}
            />
          ) : (
            <p className={styles.emptyMessage}>
              No Allocation Manager available
            </p>
          )}
        </AccordionItem>
      </Accordion>
    </div>
  );
};
